import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import { useSimulation } from '../contexts/SimulationContext';
import { useAuth } from '../contexts/AuthContext';
import apiService, { Platform } from '../services/api';
import useTrialExpirationHandler from './useTrialExpirationHandler';
import { useSubscriptionExpirationHandler } from './useSubscriptionExpirationHandler';
import useOrganizationInfo from './useOrganizationInfo';
import useAccounts, { Account } from './useAccounts';
import { useCreateSimulationData } from './useCreateSimulationData';
import { logger } from '../utils/logger';

// Define form field validation rules
const validateForm = (formData: any) => {
  const errors: Record<string, string> = {};
  
  if (!formData.content.trim()) {
    errors.content = 'Content is required';
  }
  
  if (!formData.platform) {
    errors.platform = 'Platform selection is required';
  }
  
  if (!formData.accountId) {
    errors.accountId = 'Account selection is required';
  }
  
  if (formData.simCount < 10 || formData.simCount > 50) {
    errors.simCount = 'Simulation count must be between 10 and 50';
  }
  
  return errors;
};

export interface UseCreateSimulationReturn {
  // State
  formErrors: Record<string, string>;
  platforms: Platform[];
  alertMessage: {type: 'success' | 'error' | 'warning' | 'info', message: string} | null;
  loading: boolean;
  editMode: {id: string} | null;
  
  // Handlers
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (e: SelectChangeEvent) => void;
  handleImageChange: (file: File | null) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleCloseAlert: () => void;
  
  // Hook data
  orgInfo: any;
  accounts: Account[];
  user: any;
  state: any;
  dispatch: any;
  
  // Expiration handlers
  isTrialExpiredModalOpen: boolean;
  isTrialAdmin: boolean;
  closeTrialExpiredModal: () => void;
  isSubscriptionExpiredModalOpen: boolean;
  isSubscriptionAdmin: boolean;
  closeSubscriptionExpiredModal: () => void;
}

export const useCreateSimulation = (): UseCreateSimulationReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useSimulation();
  const { user } = useAuth();
  
  // Check for landing form data passed via location state
  const landingFormData = location.state?.fromLanding ? location.state.formData : null;
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [alertMessage, setAlertMessage] = useState<{type: 'success' | 'error' | 'warning' | 'info', message: string} | null>(null);
  
  // Use custom hooks
  const { orgInfo } = useOrganizationInfo();
  const { accounts } = useAccounts();
  
  // Trial expiration handling
  const { 
    isExpiredModalOpen: isTrialExpiredModalOpen, 
    isAdmin: isTrialAdmin, 
    handleApiError: handleTrialApiError, 
    closeExpiredModal: closeTrialExpiredModal 
  } = useTrialExpirationHandler();
  
  // Subscription expiration handling
  const { 
    isExpiredModalOpen: isSubscriptionExpiredModalOpen, 
    isAdmin: isSubscriptionAdmin, 
    handleApiError: handleSubscriptionApiError, 
    closeExpiredModal: closeSubscriptionExpiredModal 
  } = useSubscriptionExpirationHandler();

  // Data loading hook
  const { platforms, loading, editMode } = useCreateSimulationData(
    dispatch,
    handleTrialApiError,
    setAlertMessage
  );
  
  // Apply landing form data when component mounts
  useEffect(() => {
    if (landingFormData) {
      // Extract sector name if it's an object (can happen after express onboarding)
      const sectorValue = typeof landingFormData.sector === 'object' && landingFormData.sector !== null
        ? (landingFormData.sector as any).name
        : landingFormData.sector || state.formData.sector;
      
      // Basic form fields
      dispatch({ 
        type: 'UPDATE_FORM', 
        payload: {
          platform: landingFormData.platform || state.formData.platform,
          content: landingFormData.content || state.formData.content,
          sector: sectorValue
        } 
      });
      
      // Handle image restoration from base64
      if (landingFormData.imageBase64 && landingFormData.imageName) {
        try {
          // Convert base64 back to File
          const base64Data = landingFormData.imageBase64 as string;
          const arr = base64Data.split(',');
          const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          const file = new File([u8arr], landingFormData.imageName, { type: mime });
          
          // Update the image in state
          dispatch({ 
            type: 'UPDATE_FORM', 
            payload: { imageFile: file }
          });
        } catch (error) {
          console.error('Error restoring image from base64:', error);
        }
      }
    }
  }, []); // Only run once on mount
  
  // Auto-select account if we have landing form data with a brand name
  useEffect(() => {
    if (landingFormData?.brand && accounts.length > 0 && !state.formData.accountId) {
      // Try to find an account that matches the brand name
      const matchingAccount = accounts.find(
        account => account.name.toLowerCase() === landingFormData.brand.toLowerCase()
      );
      
      if (matchingAccount) {
        // Extract sector name if it's an object
        const sectorValue = typeof matchingAccount.sector === 'object' && matchingAccount.sector !== null
          ? (matchingAccount.sector as any).name
          : matchingAccount.sector || landingFormData.sector || state.formData.sector;
        
        dispatch({ 
          type: 'UPDATE_FORM', 
          payload: { 
            accountId: matchingAccount.id,
            sector: sectorValue
          } 
        });
      } else if (accounts.length === 1) {
        // If no match but only one account exists, select it
        // Extract sector name if it's an object
        const sectorValue = typeof accounts[0].sector === 'object' && accounts[0].sector !== null
          ? (accounts[0].sector as any).name
          : accounts[0].sector || landingFormData.sector || state.formData.sector;
        
        dispatch({ 
          type: 'UPDATE_FORM', 
          payload: { 
            accountId: accounts[0].id,
            sector: sectorValue
          } 
        });
      }
    }
  }, [accounts, landingFormData]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    dispatch({ 
      type: 'UPDATE_FORM', 
      payload: { [name]: value } 
    });
    
    // Clear any previous error for this field
    if (formErrors[name]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    
    // If changing the account, update the sector automatically
    if (name === 'accountId' && value) {
      const selectedAccount = accounts.find(acc => acc.id === value);
      if (selectedAccount) {
        // Handle sector which can be either a string or an object
        const sectorValue = typeof selectedAccount.sector === 'object' && selectedAccount.sector !== null
          ? (selectedAccount.sector as any).name 
          : selectedAccount.sector || '';
        
        dispatch({ 
          type: 'UPDATE_FORM', 
          payload: { 
            accountId: value,
            sector: sectorValue 
          } 
        });
      } else {
        dispatch({ 
          type: 'UPDATE_FORM', 
          payload: { [name]: value } 
        });
      }
    } else {
      dispatch({ 
        type: 'UPDATE_FORM', 
        payload: { [name]: value } 
      });
    }
    
    // Clear any previous error for this field
    if (formErrors[name]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleImageChange = (file: File | null) => {
    dispatch({
      type: 'UPDATE_FORM',
      payload: {
        imageFile: file,
        // Clear preview URL when image is removed (keep it when new file is uploaded)
        imagePreviewUrl: file ? state.formData.imagePreviewUrl : ''
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm(state.formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Prevent submission if organization has no simulations left
    if (orgInfo && orgInfo.plan !== 'INTERNAL' && ((orgInfo.monthlySimQuota || 0) - (orgInfo.simUsed || 0) <= 0)) {
      setAlertMessage({
        type: 'error',
        message: 'Your organization has reached the simulation limit. Please contact your administrator to upgrade your plan.'
      });
      return;
    }
    
    // Async save-first flow: do not set global loading/progress modal
    
    try {
      // Find the selected account to get its name
      const selectedAccount = accounts.find(acc => acc.id === state.formData.accountId);
      
      if (!selectedAccount) {
        throw new Error('Selected account not found');
      }
      
      // Save-first flow: initialize record and navigate immediately
      const initRes = await apiService.initSimulation({
        content: state.formData.content,
        platform: state.formData.platform,
        accountId: state.formData.accountId,
        sender: selectedAccount.name,
        sector: state.formData.sector,
        simCount: state.formData.simCount,
        image: state.formData.imageFile || undefined,
        imageDescription: !state.formData.imageFile ? state.formData.imageDescription : undefined,
        // @ts-ignore
        ideaDraftId: (state.formData as any).ideaDraftId || undefined,
        id: editMode?.id,
        isPrivate: state.formData.isPrivate
      });
      const simulationIdToNavigate: string | undefined = initRes?.simulation;
      try {
        await apiService.waitForSimulation(simulationIdToNavigate!, { timeoutMs: 4000, intervalMs: 250 });
      } catch {}

      
      if (simulationIdToNavigate) {
        localStorage.setItem('lastSimulationId', simulationIdToNavigate);
      } else {
        // Handle case where no ID is returned, perhaps clear or log an error
        logger.error('Simulation did not return an ID. Clearing lastSimulationId.');
        localStorage.removeItem('lastSimulationId');
      }
      
      // Redirect to results page
      navigate(`/simulations/${simulationIdToNavigate}/results`);
    } catch (error: any) {
      // Check both trial and subscription expiration first
      if (!handleTrialApiError(error) && !handleSubscriptionApiError(error)) {
        dispatch({ type: 'SET_ERROR', payload: error.message });
        setAlertMessage({
          type: 'error',
          message: error.response?.data?.message || error.message || 'An error occurred while processing your simulation'
        });
      }
    } finally {
      // no-op: we did not set loading for async flow
    }
  };

  const handleCloseAlert = () => {
    setAlertMessage(null);
  };

  return {
    // State
    formErrors,
    platforms,
    alertMessage,
    loading,
    editMode,
    
    // Handlers
    handleInputChange,
    handleSelectChange,
    handleImageChange,
    handleSubmit,
    handleCloseAlert,
    
    // Hook data
    orgInfo,
    accounts,
    user,
    state,
    dispatch,
    
    // Expiration handlers
    isTrialExpiredModalOpen,
    isTrialAdmin,
    closeTrialExpiredModal,
    isSubscriptionExpiredModalOpen,
    isSubscriptionAdmin,
    closeSubscriptionExpiredModal
  };
};

export default useCreateSimulation;
